import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, ShoppingCartIcon, ArrowPathIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getAdminCarts, getAdminCart, deleteAdminCartItem, clearAdminCart } from '../../api/adminOrders';
import { Pager, PersonCard, SkeletonRow, ago, fmtMoney, inputCls } from './ui';

const PAGE_SIZE = 20;

const CartItemRow = ({ item, busy, onRemove }) => {
  const [confirm, setConfirm] = useState(false);
  return (
    <li className="flex items-center gap-3 py-2">
      {item.main_image_url ? (
        <img src={item.main_image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
      ) : (
        <div className="h-10 w-10 shrink-0 rounded-lg bg-bg-tertiary" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-text-primary">
          {item.title || (item.kind === 'china' ? `Товар 1688 #${item.product_id}` : `Объявление #${item.announcement_id}`)}
        </div>
        <div className="text-xs text-text-secondary">
          {item.quantity} {item.measurement_unit || 'шт'} × {fmtMoney(item.price_at_add)}
          {item.business_name && ` · ${item.business_name}`}
          {item.kind === 'china' && ' · Китай'}
        </div>
      </div>
      <span className="shrink-0 text-sm font-semibold text-text-primary">{fmtMoney(item.subtotal)}</span>
      {confirm ? (
        <span className="flex shrink-0 gap-1">
          <button type="button" disabled={busy} onClick={() => onRemove(item)} className="rounded-md bg-rose-500 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50">
            Удалить
          </button>
          <button type="button" onClick={() => setConfirm(false)} aria-label="Отмена" className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </span>
      ) : (
        <button type="button" onClick={() => setConfirm(true)} title="Убрать из корзины" className="shrink-0 rounded-md p-1.5 text-text-muted hover:bg-rose-500/10 hover:text-rose-500">
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </li>
  );
};

const CartDetails = ({ userId, onClose, onChanged, onOpenUser, notify }) => {
  const [cart, setCart] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setCart(await getAdminCart(userId));
    } catch (err) {
      setError(err.message);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, successText) => {
    setBusy(true);
    try {
      const res = await fn();
      setCart(res.cart);
      onChanged(res.cart);
      notify('success', successText);
    } catch (err) {
      notify('error', `Не удалось изменить корзину: ${err.detail || err.message}`);
    } finally {
      setBusy(false);
      setConfirmClear(false);
    }
  };

  if (error) return <p className="px-6 py-12 text-center text-sm text-rose-500">{error}</p>;
  if (!cart) {
    return (
      <div className="flex items-center justify-center px-6 py-16 text-sm text-text-secondary">
        <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" /> Загрузка корзины…
      </div>
    );
  }

  const allItems = [...(cart.items || []), ...(cart.china_items || [])];
  const totalAmount = (cart.total_amount || 0) + (cart.china_total_amount || 0);

  return (
    <div className="text-left">
      <div className="flex items-start justify-between gap-3 px-5 py-4">
        <div>
          <h3 className="text-lg font-bold text-text-primary">Корзина</h3>
          <p className="text-xs text-text-muted">
            {allItems.length} поз. · {fmtMoney(totalAmount)}
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="border-t border-border-primary px-5 py-4">
        <PersonCard title="Владелец" person={cart.user} onOpenUser={onOpenUser} />
      </div>
      <div className="border-t border-border-primary px-5 py-2">
        {allItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-muted">Корзина пуста</p>
        ) : (
          <ul className="divide-y divide-border-primary">
            {allItems.map((it) => (
              <CartItemRow
                key={`${it.kind}-${it.id}`}
                item={it}
                busy={busy}
                onRemove={(item) => run(() => deleteAdminCartItem(userId, item.id, item.kind), 'Товар убран из корзины')}
              />
            ))}
          </ul>
        )}
      </div>
      {allItems.length > 0 && (
        <div className="border-t border-border-primary px-5 py-4">
          {confirmClear ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3">
              <span className="text-sm text-rose-500">Очистить всю корзину пользователя?</span>
              <span className="flex gap-2">
                <button type="button" onClick={() => setConfirmClear(false)} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
                  Нет
                </button>
                <button type="button" disabled={busy} onClick={() => run(() => clearAdminCart(userId), 'Корзина очищена')} className="rounded-lg bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50">
                  Очистить
                </button>
              </span>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmClear(true)} className="inline-flex items-center gap-2 rounded-lg border border-rose-500/30 px-3 py-1.5 text-sm font-medium text-rose-500 hover:bg-rose-500/10">
              <TrashIcon className="h-4 w-4" /> Очистить корзину
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const CartsTab = ({ notify, onOpenUser }) => {
  const [carts, setCarts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const detailsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminCarts({ page, limit: PAGE_SIZE, q: appliedQ.trim() });
      setCarts(data.items || []);
      setSummary(data.summary || null);
      setTotal(data.total);
      setTotalPages(data.pages || 1);
    } catch (err) {
      setCarts([]);
      notify('error', `Ошибка загрузки корзин: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [page, appliedQ, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openCart = (userId) => {
    setSelectedUserId(userId);
    if (window.innerWidth < 1024) setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleChanged = useCallback((cart) => {
    setCarts((list) =>
      list.map((c) =>
        c.user.id === cart.user?.id
          ? {
              ...c,
              items_count: cart.items?.length || 0,
              china_items_count: cart.china_items?.length || 0,
              total_amount: (cart.total_amount || 0) + (cart.china_total_amount || 0),
            }
          : c
      )
    );
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { label: 'Непустых корзин', value: typeof total === 'number' ? total.toLocaleString('ru-RU') : '—' },
          { label: 'Товаров в корзинах', value: (summary?.items_total || 0).toLocaleString('ru-RU') },
          { label: 'На сумму', value: fmtMoney(summary?.amount_total || 0) },
        ].map((k) => (
          <div key={k.label} className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
            <div className="text-xs text-text-secondary">{k.label}</div>
            <div className="mt-1 text-xl font-bold text-text-primary">{k.value}</div>
          </div>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setAppliedQ(q);
          setPage(1);
        }}
        className="flex gap-3 rounded-block border border-border-primary bg-bg-card p-4 shadow-sm"
      >
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input type="text" placeholder="Имя, телефон или email владельца…" aria-label="Поиск корзин" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} />
        </div>
        <button type="submit" className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
          <MagnifyingGlassIcon className="h-4 w-4" /> Искать
        </button>
        <button type="button" onClick={load} disabled={loading} title="Обновить" className="rounded-lg border border-border-primary px-3 text-text-secondary hover:bg-bg-tertiary disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </form>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-5">
        <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:col-span-3">
          <ul className="divide-y divide-border-primary">
            {loading && Array.from({ length: carts.length || 6 }).map((_, i) => <SkeletonRow key={i} />)}
            {!loading && carts.length === 0 && (
              <li className="px-6 py-16 text-center">
                <ShoppingCartIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
                <h3 className="mb-1 text-base font-semibold text-text-primary">Непустых корзин нет</h3>
              </li>
            )}
            {!loading &&
              carts.map((c) => {
                const selected = selectedUserId === c.user.id;
                const count = c.items_count + c.china_items_count;
                return (
                  <li key={c.cart_id}>
                    <button
                      type="button"
                      onClick={() => openCart(c.user.id)}
                      aria-current={selected ? 'true' : undefined}
                      className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${selected ? 'bg-[#4F45E5]/[0.08]' : 'hover:bg-bg-tertiary/60'}`}
                    >
                      {selected && <span className="absolute inset-y-0 left-0 w-1 bg-accent-grad" />}
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#4F45E5]/10 text-[#7C3AED]">
                        <ShoppingCartIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-semibold text-text-primary">{c.user.name || 'Без имени'}</span>
                          <span className="font-mono text-xs text-text-muted">#{c.user.id}</span>
                        </div>
                        <div className="text-xs text-text-secondary">
                          {c.user.phone_number || c.user.email || '—'} · {count} поз.
                          {c.china_items_count > 0 && ` (Китай: ${c.china_items_count})`}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold text-text-primary">{fmtMoney(c.total_amount)}</div>
                        <div className="text-[11px] text-text-muted">{ago(c.last_activity_at)}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
          </ul>
          <Pager page={page} totalPages={totalPages} total={total} shown={carts.length} pageSize={PAGE_SIZE} loading={loading} onPage={setPage} />
        </section>

        <aside ref={detailsRef} className="lg:col-span-2">
          <div className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:sticky lg:top-4 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            {selectedUserId ? (
              <CartDetails
                key={selectedUserId}
                userId={selectedUserId}
                onClose={() => setSelectedUserId(null)}
                onChanged={handleChanged}
                onOpenUser={onOpenUser}
                notify={notify}
              />
            ) : (
              <div className="px-6 py-16 text-center">
                <ShoppingCartIcon className="mx-auto mb-3 h-10 w-10 text-text-muted" />
                <p className="text-sm text-text-secondary">Выберите корзину, чтобы увидеть товары</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CartsTab;
